// ForgotPassword.js v2.1 - Mobile Optimized
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Mail, Sun, Moon, AlertCircle, CheckCircle } from 'lucide-react';
import { URLS } from '../config/constants';
import { getTheme, useMobile } from '../utils/theme';

const PORTAL_URL = URLS.portalScript;

function ForgotPassword({ setCurrentPage, darkMode, setDarkMode }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const isMobile = useMobile();

  const theme = getTheme(darkMode);
  const { accentHighlight: accentPrimary, accentMarketingAlt: accentSecondary, gradientMarketing: accentGradient, logoLY, logoT, bgAlt: bgColor, cardBg, text: textColor, textMedium: mutedColor } = theme.colors;
  const accentError = '#dc2626';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email.includes('@')) { 
      setError('Please enter a valid email address.'); 
      return; 
    }
    
    setLoading(true);

    try {
      const payload = {
        action: 'forgotPassword',
        email: email.trim().toLowerCase()
      };

      const response = await fetch(PORTAL_URL, {
        method: 'POST',
        redirect: 'follow',
        body: new URLSearchParams({ payload: JSON.stringify(payload) })
      });

      const text = await response.text();
      let result;

      // Handle GAS redirect
      if (text.trim().startsWith('<')) {
        const match = text.match(/HREF="([^"]+)"/i);
        if (match) {
          const redirectUrl = match[1].replace(/&amp;/g, '&');
          const redirectResponse = await fetch(redirectUrl);
          const redirectText = await redirectResponse.text();
          try {
            result = JSON.parse(redirectText);
          } catch (e) {
            // If user not found, still show success (security - don't reveal if email exists)
            setSuccess(true);
            setLoading(false);
            return;
          }
        } else {
          setSuccess(true);
          setLoading(false);
          return;
        }
      } else {
        try {
// eslint-disable-next-line no-unused-vars
          result = JSON.parse(text);
        } catch (e) {
          setSuccess(true);
          setLoading(false);
          return;
        }
      }

      // Always show success for security (don't reveal if email exists)
      setSuccess(true);
      
    } catch (err) {
      console.error('Forgot password error:', err);
      // Still show success for security
      setSuccess(true);
    }

    setLoading(false);
  };

  const inputStyle = {
    width: '100%',
    padding: isMobile ? '16px' : '14px 16px',
    fontSize: '16px', // Prevents iOS zoom
    border: `2px solid ${focused ? accentPrimary : (darkMode ? '#374151' : '#e5e7eb')}`,
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    color: '#1f2937',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'all 0.2s ease',
    boxShadow: focused ? `0 0 0 3px ${accentPrimary}30` : 'none',
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bgColor }}>
      {/* Top Bar */}
      <div style={{ 
        backgroundColor: darkMode ? '#112240' : '#f1f5f9', 
        padding: isMobile ? '8px 16px' : '6px 20px', 
        display: 'flex', 
        justifyContent: 'flex-end' 
      }}>
        {setDarkMode && (
          <button
            onClick={() => setDarkMode(!darkMode)}
            aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: darkMode ? 'rgba(255,255,255,0.8)' : '#64748b',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '0.85rem',
              padding: '8px 12px',
              borderRadius: '6px',
              minHeight: '44px',
            }}
          >
            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            <span className="hide-mobile">{darkMode ? 'Light' : 'Dark'} Mode</span>
          </button>
        )}
      </div>

      {/* Header */}
      <header style={{ 
        padding: isMobile ? '12px 16px' : '16px 20px', 
        backgroundColor: darkMode ? '#0d1b2a' : '#ffffff', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}` 
      }}>
        <button
          onClick={() => setCurrentPage('portal-login')}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            backgroundColor: 'transparent', 
            border: 'none', 
            color: darkMode ? 'rgba(255,255,255,0.8)' : '#64748b', 
            fontSize: isMobile ? '0.9rem' : '1rem', 
            cursor: 'pointer', 
            padding: '10px 12px', 
            borderRadius: '8px',
            minHeight: '44px',
          }}
        >
          <ArrowLeft size={isMobile ? 18 : 20} /> {!isMobile && 'Back'}
        </button>
        <div style={{ fontSize: isMobile ? '1.1rem' : '1.25rem', fontWeight: '700', color: textColor }}>
          <span style={{ color: accentPrimary }}>Reset</span> Password
        </div>
        <button 
          onClick={() => setCurrentPage('home')} 
          style={{ 
            background: 'none', 
            border: 'none', 
            cursor: 'pointer', 
            padding: '10px',
            minHeight: '44px',
            minWidth: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ fontSize: isMobile ? '1.1rem' : '1.25rem', fontWeight: '700' }}>
            <span style={{ color: logoLY }}>ly</span><span style={{ color: logoT }}>t</span>
          </div>
        </button>
      </header>

      {/* Form */}
      <div style={{ 
        maxWidth: '400px', 
        margin: isMobile ? '24px auto' : '60px auto', 
        padding: isMobile ? '0 16px' : '0 20px' 
      }}>
        <div style={{ 
          backgroundColor: cardBg, 
          borderRadius: isMobile ? '12px' : '16px', 
          padding: isMobile ? '24px 20px' : '32px', 
          boxShadow: '0 4px 24px rgba(0,0,0,0.1)' 
        }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '20px' : '24px' }}>
            <div style={{ 
              width: isMobile ? '56px' : '64px', 
              height: isMobile ? '56px' : '64px', 
              borderRadius: '50%', 
              background: accentGradient, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              margin: '0 auto 16px' 
            }}>
              <Mail size={isMobile ? 24 : 28} color="#fff" />
            </div>
            <h2 style={{ color: textColor, margin: '0 0 8px', fontSize: isMobile ? '1.35rem' : '1.5rem' }}>Forgot Password?</h2>
            <p style={{ color: mutedColor, margin: 0, fontSize: '0.9rem' }}>Enter your email to receive a reset link</p>
            <p style={{ color: mutedColor, margin: '8px 0 0', fontSize: '0.7rem', opacity: 0.5 }}>v2.1</p>
          </div>

          {success ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ 
                width: isMobile ? '56px' : '64px', 
                height: isMobile ? '56px' : '64px', 
                borderRadius: '50%', 
                backgroundColor: `${accentSecondary}20`, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                margin: '0 auto 16px' 
              }}>
                <CheckCircle size={isMobile ? 28 : 32} color={accentSecondary} />
              </div>
              <h3 style={{ color: textColor, margin: '0 0 8px' }}>Check Your Email</h3>
              <p style={{ color: mutedColor, fontSize: '0.9rem', margin: '0 0 24px' }}>
                If an account exists for {email}, you'll receive a password reset link shortly.
              </p>
              <button
                onClick={() => setCurrentPage('portal-login')}
                style={{ 
                  width: '100%', 
                  padding: isMobile ? '16px' : '14px', 
                  background: accentGradient, 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: '8px', 
                  fontSize: '1rem', 
                  fontWeight: '600', 
                  cursor: 'pointer',
                  minHeight: '50px',
                }}
              >
                Back to Sign In
              </button>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: textColor }}>Email Address</label>
                  <input 
                    type="email" 
                    value={email} 
                    onChange={(e) => setEmail(e.target.value)} 
                    onFocus={() => setFocused(true)} 
                    onBlur={() => setFocused(false)} 
                    placeholder="you@company.com" 
                    required 
                    style={inputStyle}
                    autoComplete="email"
                    autoCapitalize="none"
                  />
                </div>
                {error && (
                  <div role="alert" style={{ marginBottom: '20px', padding: '12px', backgroundColor: `${accentError}20`, borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AlertCircle size={18} color={accentError} />
                    <span style={{ color: accentError, fontSize: '0.9rem' }}>{error}</span>
                  </div>
                )}
                <button 
                  type="submit" 
                  disabled={loading} 
                  style={{ 
                    width: '100%', 
                    padding: isMobile ? '16px' : '14px', 
                    background: loading ? (darkMode ? '#374151' : '#9ca3af') : accentGradient, 
                    color: '#fff', 
                    border: 'none', 
                    borderRadius: '8px', 
                    fontSize: '1rem', 
                    fontWeight: '600', 
                    cursor: loading ? 'not-allowed' : 'pointer',
                    minHeight: '50px',
                  }}
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>
              <div style={{ marginTop: '24px', textAlign: 'center' }}>
                <button 
                  onClick={() => setCurrentPage('portal-login')} 
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: accentPrimary, 
                    cursor: 'pointer', 
                    fontSize: '0.9rem',
                    padding: '12px',
                    minHeight: '44px',
                  }}
                >
                  Remember your password? Sign in
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
